import { getUserSelector } from "@/redux/user/userSlice";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { ONBOARDING_TOTAL, onboardingDone } from "@repo/shared/myClub";
import { useCanUseClubImportFeature } from "@repo/shared/redux";
import { useEffect } from "react";
import { useSelector } from "react-redux";

/*
  The last progress the account gave, so the nav badge has a figure before
  the profile has loaded instead of counting every step as undone and
  blinking. Per browser; the account's ticks replace it once they're in.
*/
const PROGRESS_KEY = "MY_CLUB_ONBOARDING_PROGRESS";

/**
 * How far the user is through My Club's onboarding, or null when there is
 * nothing to show: no club yet, or every step done. `done` counts the import.
 */
export const useMyClubOnboardingProgress = () => {
  const hasClub = useCanUseClubImportFeature("see-own-players");
  const { user, status } = useSelector(getUserSelector);
  const profileLoaded = status === "fulfilled";
  const live = onboardingDone(user?.usedFeatures);

  useEffect(() => {
    if (profileLoaded && hasClub) safeSetItem(PROGRESS_KEY, String(live));
  }, [profileLoaded, hasClub, live]);

  if (!hasClub) return null;
  // Before the profile, the last figure this browser saw; none, nothing.
  const done = profileLoaded ? live : Number(safeGetItem(PROGRESS_KEY));
  if (!done || done >= ONBOARDING_TOTAL) return null;
  return { done, total: ONBOARDING_TOTAL };
};

/**
 * My Club's onboarding progress as a notification-style count of the steps
 * done ("1/9") beside its nav entries, so the next step is one tap away.
 */
export const OnboardingProgressBadge = () => {
  const progress = useMyClubOnboardingProgress();
  if (!progress) return null;
  return (
    // The progress bar's green, so the count reads as the same progress.
    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[linear-gradient(90deg,var(--color-streamline-progress-from),var(--color-streamline-progress-to))] px-1.5 font-sans text-xs font-semibold leading-none tabular-nums text-background">
      {progress.done}/{progress.total}
    </span>
  );
};
